import React, { useCallback, useEffect, useState } from "react";
import { notBlank } from "../../util/ext";
import {
  append,
  find,
  ifElse,
  includes,
  lensPath,
  lensProp,
  map,
  mergeRight,
  not,
  pick,
  propEq,
  reject,
  set,
} from "ramda";
import { useAudiences } from "../cspServices";
import { makeStyles } from "@material-ui/styles";
import { useErrorBox } from "../../manage-organizations/edit/use-errorBox";
import {
  validateProjectDetails,
  validateProjectDetailsField,
} from "../cspProjectValidation";

import Button from "@material-ui/core/Button";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import LuxonUtils from "@date-io/luxon";
import FormControl from "@material-ui/core/FormControl";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormGroup,
  FormHelperText,
  FormLabel,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";

import OrganizationAutoComplete from "../../user-admin/OrganizationAutoComplete";

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: "1.8rem",
  },

  multiPicker: {
    "& label.MuiFormLabel-root": {
      backgroundColor: "#fff",
      marginLeft: "-5px",
      padding: "0 6px",
    },
  },
}));

const EditProjectDetailsDialog = ({ open, details, onCancel, onApply }) => {
  const classes = useStyles();

  const [fields, setFields] = useState({});
  useEffect(() => {
    if (open && notBlank(details)) {
      setFields(
        pick(
          [
            "autoApprove",
            "projectId",
            "projectName",
            "projectKey",
            "forceInactive",
            "termsUri",
            "redirectUri",
            "collectPurchaseLink",
            "useDefaultCategories",
            "startDate",
            "dateExpires",
            "defaultLibraryId",
            "audiences",
            "hideCoverImageUpload",
            "defaultLanguage",
            "defaultPublicationDateFormat",
          ],
          details,
        ),
      );
    } else {
      setFields({});
    }
  }, [open, details]);

  const audiencesResponse = useAudiences();
  const [audiences, setAudiences] = useState([]);
  useEffect(() => {
    if (audiencesResponse.isSuccess) {
      setAudiences(audiencesResponse.data);
    }
  }, [audiencesResponse.isSuccess, audiencesResponse.data]);

  const {
    addValidationError,
    removeValidationError,
    setValidationErrors,
    hasValidationError,
    firstValidationError,
    ErrorBox: DialogErrors,
  } = useErrorBox();

  const handleBlur = (field, value) => {
    const errors = validateProjectDetailsField(field, value);
    if (notBlank(errors)) {
      addValidationError(errors);
    } else {
      removeValidationError(field);
    }
  };

  const handleTextChange = path => (event, value) => {
    const val = notBlank(value)
      ? value
      : notBlank(event)
      ? event.target.value
      : event;

    setFields(prev => set(lensPath(path), val, prev));
  };

  const handleCheckboxChange = field => event => {
    const value = event.target.checked;
    setFields(prev => set(lensProp(field), value, prev));
  };

  const handleFieldChange = field => value => {
    setFields(prev => set(lensProp(field), value, prev));
  };

  const handleDefaultLanguageChange = event => {
    const value = event.target.value;
    setFields(set(lensProp("defaultLanguage"), value));
  };

  const handleDefaultPubDateFormatChange = event => {
    const value = event.target.value;
    setFields(set(lensProp("defaultPublicationDateFormat"), value));
  };

  const handleAudienceChange = useCallback(
    event => {
      const value = find(propEq("name", event.target.name), audiences);
      const toggle = ifElse(
        includes(value),
        reject(propEq("name", value.name)),
        append(value),
      );

      setFields(prev =>
        set(lensProp("audiences"), toggle(prev.audiences), prev),
      );
    },
    [audiences],
  );

  const handleApply = useCallback(async () => {
    const validationErrors = validateProjectDetails(fields);
    if (notBlank(validationErrors)) {
      setValidationErrors(validationErrors);
    } else {
      if (details.useDefaultCategories && not(fields.useDefaultCategories)) {
        onApply(mergeRight(fields, { categories: [] }));
      } else {
        onApply(fields);
      }
    }
  }, [fields, details, onApply, setValidationErrors]);

  return (
    <Dialog maxWidth="md" open={open}>
      <DialogTitle id="edit-organization-detail-dialog-title">
        Edit Project Details
      </DialogTitle>
      <DialogContent>
        <DialogErrors />
        <MuiPickersUtilsProvider utils={LuxonUtils}>
          <Grid container spacing={6}>
            <Grid item sm={9}>
              <TextField
                autoFocus
                id="project-name"
                fullWidth
                label="Project Name"
                InputProps={{
                  classes: { root: classes.title },
                }}
                value={fields.projectName}
                onChange={handleTextChange(["projectName"])}
                error={hasValidationError("projectName")}
                helperText={firstValidationError("projectName")}
                onBlur={() => handleBlur("projectName", fields.projectName)}
              />
            </Grid>
            <Grid item sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={fields.forceInactive}
                    onChange={handleCheckboxChange("forceInactive")}
                    name="force-inactive"
                    inputProps={{ "aria-label": "Select" }}
                  />
                }
                label="Force Inactive"
              />
            </Grid>
            <Grid item sm={9}>
              <TextField
                id="project-key"
                fullWidth
                label="Project Key"
                InputProps={{
                  classes: { root: classes.title },
                }}
                value={fields.projectKey}
                onChange={handleTextChange(["projectKey"])}
                error={hasValidationError("projectKey")}
                helperText={firstValidationError("projectKey")}
                onBlur={() => handleBlur("projectKey", fields.projectKey)}
              />
            </Grid>
            <Grid item sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={fields.collectPurchaseLink}
                    onChange={handleCheckboxChange("collectPurchaseLink")}
                    name="collect-purchase-link"
                    inputProps={{ "aria-label": "Select" }}
                  />
                }
                label="Collect Purchase Link"
              />
            </Grid>
            <Grid item sm={9}>
              <TextField
                id="terms-uri"
                fullWidth
                label="Terms URI"
                InputProps={{
                  classes: { root: classes.title },
                }}
                value={fields.termsUri}
                onChange={handleTextChange(["termsUri"])}
                error={hasValidationError("termsUri")}
                helperText={firstValidationError("termsUri")}
                onBlur={() => handleBlur("termsUri", fields.termsUri)}
              />
            </Grid>
            <Grid item sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={fields.hideCoverImageUpload}
                    onChange={handleCheckboxChange("hideCoverImageUpload")}
                    name="hide-cover-image-upload"
                    inputProps={{ "aria-label": "Select" }}
                  />
                }
                label="Hide Cover Image Upload"
              />
            </Grid>
            <Grid item sm={9}>
              <TextField
                id="redirect-uri"
                fullWidth
                label="Redirect URI"
                InputProps={{
                  classes: { root: classes.title },
                }}
                value={fields.redirectUri}
                onChange={handleTextChange(["redirectUri"])}
                error={hasValidationError("redirectUri")}
                helperText={firstValidationError("redirectUri")}
                onBlur={() => handleBlur("redirectUri", fields.redirectUri)}
              />
            </Grid>

            <Grid item sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={fields.autoApprove}
                    onChange={handleCheckboxChange("autoApprove")}
                    name="auto-approve"
                    inputProps={{ "aria-label": "Select" }}
                  />
                }
                label="Auto Approve"
              />
            </Grid>

            <Grid item sm={8}>
              <OrganizationAutoComplete
                value={fields.defaultLibraryId}
                filterFunction={org => org.active}
                onChange={handleFieldChange("defaultLibraryId")}
                onBlur={() =>
                  handleBlur("defaultLibraryId", fields.defaultLibraryId)
                }
                error={hasValidationError("defaultLibraryId")}
                helperText={firstValidationError("defaultLibraryId")}
              />
            </Grid>

            <Grid item sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={fields.useDefaultCategories}
                    onChange={handleCheckboxChange("useDefaultCategories")}
                    name="use-default-categories"
                    inputProps={{
                      "aria-label": "select use default categories",
                    }}
                  />
                }
                label="Use Default Categories"
              />
            </Grid>

            <Grid item sm={6}>
              <KeyboardDatePicker
                disableToolbar
                fullWidth
                autoOk
                id="start-date"
                format="MM/dd/yyyy"
                label="Start Date"
                variant="inline"
                value={fields.startDate || null}
                onChange={handleTextChange(["startDate"])}
                KeyboardButtonProps={{
                  "aria-label": "change start date",
                }}
              />
            </Grid>

            <Grid item sm={6}>
              <KeyboardDatePicker
                disableToolbar
                fullWidth
                autoOk
                id="end-date"
                format="MM/dd/yyyy"
                label="End Date"
                variant="inline"
                value={fields.dateExpires || null}
                onChange={handleTextChange(["dateExpires"])}
                KeyboardButtonProps={{
                  "aria-label": "change end date",
                }}
              />
            </Grid>

            <Grid item sm={3}>
              <FormControl
                id="audiences"
                error={hasValidationError("audiences")}
                component="fieldset"
              >
                <FormLabel component="legend">Audiences</FormLabel>
                <FormGroup>
                  {map(
                    aud => (
                      <FormControlLabel
                        key={aud.name}
                        control={
                          <Checkbox
                            color="primary"
                            checked={includes(aud, fields.audiences)}
                            onChange={handleAudienceChange}
                            name={aud.name}
                            inputProps={{
                              "aria-label": `select audience ${aud.display}`,
                            }}
                          />
                        }
                        label={aud.display}
                      />
                    ),
                    audiences,
                  )}
                </FormGroup>
                <FormHelperText>
                  {firstValidationError("audiences")}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item sm={4}>
              <FormControl fullWidth>
                <InputLabel htmlFor="default-project-language">
                  Default Project Language
                </InputLabel>
                <Select
                  labelId="default-project-language"
                  id="default-language"
                  value={fields.defaultLanguage}
                  onChange={handleDefaultLanguageChange}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                >
                  <MenuItem value={null}>
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="es">Spanish</MenuItem>
                  <MenuItem value="ar">Arabic</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item sm={4}>
              <FormControl fullWidth>
                <InputLabel htmlFor="default-publication-date-format">
                  Default Publication Date Format
                </InputLabel>
                <Select
                  labelId="default-publication-date-format"
                  id="default-publication-date-format"
                  value={fields.defaultPublicationDateFormat}
                  onChange={handleDefaultPubDateFormatChange}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                >
                  <MenuItem value={null}>
                    <em>Both</em>
                  </MenuItem>
                  <MenuItem value="year">Publication Year</MenuItem>
                  <MenuItem value="date">Publication Date</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </MuiPickersUtilsProvider>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditProjectDetailsDialog;
